// ============================================
// LOGIN PAGE - Email OTP
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Shield, ArrowRight } from 'lucide-react';
import { Layout } from '../../components/layout';
import { Button, Input, Card, CardContent, Alert } from '../../components/ui';
import { useAuthStore } from '../../store';
import { ROUTES, SUCCESS_MESSAGES, ERROR_MESSAGES } from '../../constants';
import styles from './LoginPage.module.css';

export function LoginPage() {
  const navigate = useNavigate();
  const { sendOtp, isLoading } = useAuthStore();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      await sendOtp(email);
      setSuccess(SUCCESS_MESSAGES.OTP_SENT);
      // Store email for verification page
      sessionStorage.setItem('pending_email', email);
      navigate(ROUTES.VERIFY_OTP);
    } catch (err) {
      console.error('OTP send error:', err);
      setError(ERROR_MESSAGES.GENERIC);
    }
  };

  return (
    <Layout maxWidth="sm">
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.iconWrapper}>
            <Mail size={32} />
          </div>
          <h1 className={styles.title}>Welcome</h1>
          <p className={styles.subtitle}>
            Enter your email to get started with your personalized device recommendation
          </p>
        </div>

        <Card variant="elevated">
          <CardContent>
            <form onSubmit={handleSubmit} className={styles.form}>
              {error && <Alert variant="error">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}

              <Input
                type="email"
                label="Email Address"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                autoFocus
              />

              <Button 
                type="submit" 
                fullWidth 
                isLoading={isLoading}
                size="lg"
              >
                Continue with Email
                <ArrowRight size={18} />
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className={styles.trustBadge}>
          <Shield size={16} />
          <span>We'll send you a one-time code. No password needed.</span>
        </div>
      </div>
    </Layout>
  );
}
